/**
 * Utility functions for managing Twilio Video room URLs and operations
 */

export interface RoomUrlOptions {
   roomSid: string;
   roomName: string;
   eventId?: string;
   eventTitle?: string;
}

/**
 * Generate a shareable URL for joining a Twilio Video room
 * Users can click this URL to join the video call
 */
export const generateRoomUrl = (options: RoomUrlOptions): string => {
   const { roomSid, roomName, eventId, eventTitle } = options;

   // Create a base URL for the room
   const baseUrl = window.location.origin;

   // Use roomSid as the primary identifier for the URL
   const roomUrl = `${baseUrl}/join-room/${roomSid}`;

   // Add query parameters for additional context
   const params = new URLSearchParams();

   if (roomName) {
      params.append('name', roomName);
   }

   if (eventId) {
      params.append('eventId', eventId);
   }

   if (eventTitle) {
      params.append('title', encodeURIComponent(eventTitle));
   }

   const queryString = params.toString();
   return queryString ? `${roomUrl}?${queryString}` : roomUrl;
};

/**
 * Parse room information from a room URL
 */
export const parseRoomUrl = (url: string): { roomSid: string; params: URLSearchParams } | null => {
   try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');

      // Expected format: /join-room/{roomSid}
      if (pathParts.length >= 3 && pathParts[1] === 'join-room') {
         const roomSid = pathParts[2];
         const params = new URLSearchParams(urlObj.search);

         return { roomSid, params };
      }

      return null;
   } catch (error) {
      console.error('Error parsing room URL:', error);
      return null;
   }
};

/**
 * Copy room URL to clipboard
 */
export const copyRoomUrlToClipboard = async (roomUrl: string): Promise<boolean> => {
   try {
      if (navigator.clipboard && window.isSecureContext) {
         await navigator.clipboard.writeText(roomUrl);
         return true;
      } else {
         // Fallback for older browsers
         const textArea = document.createElement('textarea');
         textArea.value = roomUrl;
         textArea.style.position = 'fixed';
         textArea.style.left = '-999999px';
         textArea.style.top = '-999999px';
         document.body.appendChild(textArea);
         textArea.focus();
         textArea.select();

         const result = document.execCommand('copy');
         textArea.remove();

         return result;
      }
   } catch (error) {
      console.error('Error copying to clipboard:', error);
      return false;
   }
};

/**
 * Check if a room URL is valid
 */
export const isValidRoomUrl = (url: string): boolean => {
   const parsed = parseRoomUrl(url);
   return parsed !== null && parsed.roomSid.length > 0;
};

/**
 * Generate a QR code URL for a room (using QR code service)
 */
export const generateRoomQRCode = (roomUrl: string): string => {
   // Using a simple QR code API - you might want to use a different service
   const encodedUrl = encodeURIComponent(roomUrl);
   return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`;
};

/**
 * Create a calendar event for the room
 */
export const createCalendarEvent = (options: {
   roomUrl: string;
   eventTitle: string;
   startDate: Date;
   endDate: Date;
   description?: string;
}): string => {
   const { roomUrl, eventTitle, startDate, endDate, description } = options;

   const formatDate = (date: Date): string => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
   };

   const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventTitle,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      details: `${description || 'Join the video call'}\n\nJoin URL: ${roomUrl}`,
      location: roomUrl,
   });

   return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Share room URL via Web Share API (if supported)
 */
export const shareRoomUrl = async (options: {
   roomUrl: string;
   eventTitle: string;
   description?: string;
}): Promise<boolean> => {
   const { roomUrl, eventTitle, description } = options;

   if (navigator.share) {
      try {
         await navigator.share({
            title: eventTitle,
            text: description || 'Join this video call',
            url: roomUrl,
         });
         return true;
      } catch (error) {
         console.error('Error sharing:', error);
         return false;
      }
   }

   // Fallback: copy to clipboard
   return await copyRoomUrlToClipboard(roomUrl);
};
